import { useCallback, useRef } from "react";

import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

/**
 * Returns a constant reference to a callback which can be safely added
 * to dependency arrays without triggering the effect or memoization.
 * Maintains an up to date copy of the provided function to avoid
 * referencing stale state/props at calltime.
 */
const useConstantRefCallback = <Args extends unknown[], Result>(
	fn: (...args: Args) => Result,
) => {
	const ref = useRef(fn);

	// copy a ref to the callback scoped to the current state/props on each render
	useIsomorphicLayoutEffect(() => {
		ref.current = fn;
	});

	return useCallback((...args: Args) => ref.current(...args), []);
};

export default useConstantRefCallback;
